import { createTheme, alpha } from "@mui/material/styles";

const colors = {
  rose: {
    50:  "#fef0f5",
    100: "#fcdde9",
    200: "#f5b5cd",
    300: "#ea84a9",
    400: "#d96088",
    500: "#c4466e",
    600: "#9e4562",
    700: "#8f3059",
    800: "#6b2c47",
    900: "#3d1228",
  },
  peach: {
    50:  "#fef6f2",
    100: "#fde8dc",
    200: "#f8c9b0",
    300: "#efa480",
    400: "#e0845a",
    500: "#a5492b",
    600: "#b86044",
    700: "#9a4a34",
    800: "#7a3628",
    900: "#4f2018",
  },
  petal: {
    0:   "#ffffff",
    50:  "#fdf5f8",
    100: "#f7eef3",
    200: "#eedde8",
    300: "#dfc9d8",
    400: "#8a5c7c",
    500: "#89647d",
    600: "#8c6a80",
    700: "#6a4e62",
    800: "#4a3446",
    900: "#1a0d12",
  },
};

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: colors.rose[600],
      light: colors.rose[50],
      dark: colors.rose[800],
      contrastText: "#ffffff",
    },
    secondary: {
      main: colors.peach[500],
      light: colors.peach[100],
      dark: colors.peach[700],
      contrastText: "#ffffff",
    },
    background: {
      default: colors.petal[50],
      paper: "#ffffff",
    },
    text: {
      primary: colors.petal[900],
      secondary: colors.petal[700],
      disabled: colors.petal[400],
    },
    divider: colors.petal[200],
    success: { main: "#9e4562", light: "#fef0f5" },
    warning: { main: colors.peach[500], light: colors.peach[50] },
    error: { main: "#ad3c3c", light: "#fbeaea" },
  },

  typography: {
    fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif",
    h1: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: "3.75rem",
      fontWeight: 600,
      lineHeight: 1.1,
      letterSpacing: "-0.01em",
      "@media (max-width: 600px)": { fontSize: "2.4rem" },
    },
    h2: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: "3rem",
      fontWeight: 600,
      lineHeight: 1.15,
      "@media (max-width: 600px)": { fontSize: "2rem" },
    },
    h3: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: "2.25rem",
      fontWeight: 600,
      lineHeight: 1.25,
    },
    h4: {
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: "1.75rem",
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h5: { fontSize: "1.2rem", fontWeight: 600, lineHeight: 1.4 },
    h6: { fontSize: "1rem", fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: "1rem", fontWeight: 400, lineHeight: 1.7 },
    body2: { fontSize: "0.9rem", fontWeight: 400, lineHeight: 1.65 },
    subtitle1: { fontSize: "1.1rem", fontWeight: 400, lineHeight: 1.6 },
    button: {
      fontSize: "0.875rem",
      fontWeight: 600,
      textTransform: "none",
      letterSpacing: "0.02em",
    },
    caption: { fontSize: "0.78rem", lineHeight: 1.5 },
    overline: {
      fontSize: "0.68rem",
      fontWeight: 700,
      lineHeight: 1.5,
      textTransform: "uppercase",
      letterSpacing: "0.14em",
    },
  },

  shape: { borderRadius: 6 },

  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
          fontWeight: 600,
          letterSpacing: "0.02em",
          textTransform: "none",
          fontSize: "0.875rem",
          transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
        },
        contained: {
          background: `linear-gradient(135deg, ${colors.rose[500]} 0%, ${colors.rose[800]} 100%)`,
          color: "#ffffff",
          padding: "12px 28px",
          "&:hover": {
            background: `linear-gradient(135deg, ${colors.rose[600]} 0%, ${colors.rose[900]} 100%)`,
            transform: "translateY(-1px)",
            boxShadow: "0 8px 24px rgba(107,44,71,0.3)",
          },
          "&:active": { transform: "translateY(0)" },
        },
        outlined: {
          border: `1.5px solid ${colors.rose[600]}`,
          color: colors.rose[600],
          padding: "11px 27px",
          "&:hover": {
            background: alpha(colors.rose[600], 0.06),
            border: `1.5px solid ${colors.rose[500]}`,
          },
        },
      },
      defaultProps: { disableElevation: true },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          border: `1px solid ${colors.petal[200]}`,
          boxShadow: "none",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { boxShadow: "none", backgroundColor: "transparent" },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: colors.petal[200] },
      },
    },
  },
});

export const designTokens = colors;
export { colors, theme as default };
